import { useState, useRef, useEffect, useCallback } from "react";
import posthog from "@/lib/posthog";
import { trackEvent } from "@/lib/analytics";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AppHeader } from "@/components/app-header";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  FlipHorizontal,
  Trash2,
  Play,
  Gauge,
  Repeat,
  Library,
  Video,
  Pencil,
  Check,
  X,
  Loader2,
  StickyNote,
  Plus,
  Lock,
  ListPlus,
  List,
  Share2,
  Link as LinkIcon,
  Unlink,
  Music,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { UpgradeModal } from "@/components/upgrade-modal";
import type { SavedVideo } from "@shared/models/savedVideos";

type FilterType = "all" | "youtube" | "upload" | "audio";
type ViewMode = "videos" | "playlists";

export default function LibraryPage() {
  useEffect(() => { document.title = "My Library | DanceMirror"; }, []);
  const auth = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [filter, setFilter] = useState<FilterType>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("videos");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const [notesOpenId, setNotesOpenId] = useState<number | null>(null);
  const [notesText, setNotesText] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const notesSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notesVideoIdRef = useRef<number | null>(null);

  // Upgrade modal state
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState("playlists");

  // Playlist state
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [editingPlaylistId, setEditingPlaylistId] = useState<number | null>(null);
  const [editPlaylistName, setEditPlaylistName] = useState("");
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<number | null>(null);

  const isPaid = ["monthly", "yearly", "lifetime"].includes(auth.user?.subscriptionTier ?? "");

  const { data: videos, isLoading } = useQuery<SavedVideo[]>({
    queryKey: ["/api/videos"],
    enabled: auth.isAuthenticated,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasProcessing = data.some(
        (v) => v.videoType === "upload" && v.cloudflareStreamId && !v.thumbnailUrl
      );
      return hasProcessing ? 5000 : false;
    },
  });

  const { data: storageData } = useQuery<{
    usedSeconds: number;
    warnThresholdSeconds: number;
    limitSeconds: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  }>({
    queryKey: ["/api/videos/storage-usage"],
    enabled: auth.isAuthenticated && isPaid,
  });

  const { data: playlistsData } = useQuery<any[]>({
    queryKey: ["/api/playlists"],
    enabled: auth.isAuthenticated && isPaid,
  });

  const { data: playlistItemsData } = useQuery<any[]>({
    queryKey: ["/api/playlists", selectedPlaylistId, "videos"],
    queryFn: async () => {
      if (!selectedPlaylistId) return [];
      const res = await apiRequest("GET", `/api/playlists/${selectedPlaylistId}/items`);
      return res.json();
    },
    enabled: selectedPlaylistId !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/videos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/videos/storage-usage"] });
      toast({ title: "Video removed from library" });
      posthog.capture('video_deleted');
    },
    onError: () => {
      toast({ title: "Failed to delete video", variant: "destructive" });
    },
  });

  const renameMutation = useMutation({
    mutationFn: async ({ id, title }: { id: number; title: string }) => {
      await apiRequest("PATCH", `/api/videos/${id}`, { title });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      setEditingId(null);
      toast({ title: "Video renamed" });
    },
    onError: () => {
      toast({ title: "Failed to rename video", variant: "destructive" });
    },
  });

  const createPlaylistMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/playlists", { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playlists"] });
      setNewPlaylistName("");
      setCreatingPlaylist(false);
      toast({ title: "Playlist created" });
    },
    onError: (error: any) => {
      const msg = error?.message ?? "";
      if (msg.startsWith("403")) {
        toast({ title: "Upgrade required", description: "Playlists require a paid plan.", variant: "destructive" });
      } else {
        toast({ title: "Failed to create playlist", description: "Please try again.", variant: "destructive" });
      }
    },
  });

  const renamePlaylistMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const res = await apiRequest("PATCH", `/api/playlists/${id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playlists"] });
      setEditingPlaylistId(null);
      toast({ title: "Playlist renamed" });
    },
    onError: (error: any) => {
      const msg = error?.message ?? "";
      if (msg.startsWith("403")) {
        toast({ title: "Upgrade required", description: "Playlists require a paid plan.", variant: "destructive" });
      } else {
        toast({ title: "Failed to rename playlist", description: "Please try again.", variant: "destructive" });
      }
    },
  });

  const deletePlaylistMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/playlists/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playlists"] });
      if (selectedPlaylistId !== null) setSelectedPlaylistId(null);
      toast({ title: "Playlist deleted" });
    },
    onError: (error: any) => {
      const msg = error?.message ?? "";
      if (msg.startsWith("403")) {
        toast({ title: "Upgrade required", description: "Playlists require a paid plan.", variant: "destructive" });
      } else {
        toast({ title: "Failed to delete playlist", description: "Please try again.", variant: "destructive" });
      }
    },
  });

  const addToPlaylistMutation = useMutation({
    mutationFn: async ({ playlistId, videoId }: { playlistId: number; videoId: number }) => {
      const res = await apiRequest("POST", `/api/playlists/${playlistId}/videos`, { videoId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playlists"] });
      toast({ title: "Added to playlist" });
    },
    onError: (error: any) => {
      const msg = error?.message ?? "";
      if (msg.startsWith("409") || msg.includes("already in playlist")) {
        toast({ title: "Already in playlist", variant: "destructive" });
      } else if (msg.startsWith("403")) {
        toast({ title: "Upgrade required", description: "Playlists require a paid plan.", variant: "destructive" });
      } else {
        toast({ title: "Failed to add to playlist", variant: "destructive" });
      }
    },
  });

  const removeFromPlaylistMutation = useMutation({
    mutationFn: async ({ playlistId, videoId }: { playlistId: number; videoId: number }) => {
      await apiRequest("DELETE", `/api/playlists/${playlistId}/videos/${videoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/playlists"] });
      toast({ title: "Removed from playlist" });
    },
    onError: () => {
      toast({ title: "Failed to remove from playlist", variant: "destructive" });
    },
  });

  const shareMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/videos/${id}/share`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      posthog.capture('share_link_copied', { video_type: 'upload' });
      trackEvent("share_link_created", { source_type: "upload" });
      navigator.clipboard.writeText(data.shareUrl).then(() => {
        trackEvent("share_link_copied", {
          source_type: "upload",
          location: "library",
        });
        toast({ title: "Share link copied!", description: "Anyone with this link can view the video." });
      }).catch(() => {
        toast({ title: "Share link generated", description: data.shareUrl });
      });
    },
    onError: (error: any) => {
      const msg = error?.message ?? "";
      if (msg.startsWith("403")) {
        toast({ title: "Upgrade required", description: "Sharing requires a paid plan.", variant: "destructive" });
      } else {
        toast({ title: "Failed to generate share link", variant: "destructive" });
      }
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: async ({ id, isShared }: { id: number; isShared: boolean }) => {
      const res = await apiRequest("PATCH", `/api/videos/${id}/share`, { isShared });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      toast({ title: data.isShared ? "Sharing re-enabled" : "Sharing disabled" });
    },
    onError: () => {
      toast({ title: "Failed to update sharing", variant: "destructive" });
    },
  });

  useEffect(() => {
    if (editingId !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    if (!videos) return;
    const processingVideos = videos.filter(
      (v) => v.videoType === "upload" && v.cloudflareStreamId && !v.thumbnailUrl
    );
    if (processingVideos.length === 0) return;

    processingVideos.forEach((v) => {
      fetch(`/api/cloudflare/status/${v.cloudflareStreamId}`, { credentials: "same-origin" }).catch(() => {});
    });
  }, [videos]);

  const saveNotes = useCallback(async (videoId: number, text: string) => {
    if (notesVideoIdRef.current !== videoId) return;
    setNotesSaving(true);
    try {
      await apiRequest("PATCH", `/api/videos/${videoId}`, { notes: text || null });
      if (notesVideoIdRef.current === videoId) {
        setNotesDirty(false);
        setNotesSaved(true);
        setTimeout(() => setNotesSaved(false), 2000);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
    } catch {
      toast({ title: "Failed to save notes", variant: "destructive" });
    } finally {
      if (notesVideoIdRef.current === videoId) {
        setNotesSaving(false);
      }
    }
  }, [toast]);

  const handleNotesChange = useCallback((videoId: number, text: string) => {
    setNotesText(text);
    setNotesDirty(true);
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
    }
    notesSaveTimerRef.current = setTimeout(() => {
      saveNotes(videoId, text);
    }, 1000);
  }, [saveNotes]);

  const closeNotes = useCallback(() => {
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = null;
    }
    const vid = notesVideoIdRef.current;
    if (vid !== null && notesDirty) {
      saveNotes(vid, notesText);
    }
    notesVideoIdRef.current = null;
    setNotesOpenId(null);
    setNotesText("");
    setNotesDirty(false);
  }, [notesDirty, notesText, saveNotes]);

  const toggleNotes = (video: SavedVideo) => {
    if (!isPaid) return;
    if (notesOpenId === video.id) {
      closeNotes();
    } else {
      if (notesOpenId !== null) {
        closeNotes();
      }
      notesVideoIdRef.current = video.id;
      setNotesOpenId(video.id);
      setNotesText(video.notes || "");
      setNotesDirty(false);
      setNotesSaving(false);
    }
  };

  const handleOpenVideo = (video: SavedVideo) => {
    if (editingId === video.id) return;
    if (notesOpenId === video.id) return;
    if (video.videoType === "audio") {
      window.scrollTo(0, 0);
      setLocation(`/audio?saved=${video.id}`);
      return;
    }
    const params = new URLSearchParams();
    if (video.videoType === "youtube" && video.youtubeId) {
      params.set("v", video.youtubeId);
      params.set("savedId", String(video.id));
    } else if (video.videoType === "upload" && (video.filePath || video.cloudflareStreamId)) {
      params.set("uploaded", String(video.id));
    }
    if (video.settings) {
      const s = video.settings as any;
      if (s.isMirrored) params.set("mirror", "1");
      if (s.showLeftRight) params.set("lr", "1");
      if (s.playbackSpeed && s.playbackSpeed !== 1) params.set("speed", String(s.playbackSpeed));
      if (s.loopStart != null) params.set("loopA", String(s.loopStart));
      if (s.loopEnd != null) params.set("loopB", String(s.loopEnd));
    }
    params.set('from', 'library');
    window.scrollTo(0, 0);
    setLocation(`/?${params.toString()}`);
  };

  const getThumbnail = (video: SavedVideo) => {
    if (video.thumbnailUrl) return video.thumbnailUrl;
    if (video.youtubeId) return `https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`;
    return null;
  };

  const startEditing = (video: SavedVideo) => {
    setEditingId(video.id);
    setEditTitle(video.title || (video.youtubeId ? `YouTube: ${video.youtubeId}` : "Uploaded Video"));
  };

  const saveEdit = () => {
    if (editingId === null || !editTitle.trim()) return;
    renameMutation.mutate({ id: editingId, title: editTitle.trim() });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
  };

  // Filter videos: by type and optionally by playlist
  const filteredVideos = videos?.filter((v) => {
    if (selectedPlaylistId !== null && isPaid) {
      if (!playlistItemsData?.includes(v.id)) return false;
    }
    if (filter === "all") return true;
    return v.videoType === filter;
  });

  const youtubeCount = videos?.filter((v) => v.videoType === "youtube").length ?? 0;
  const uploadCount = videos?.filter((v) => v.videoType === "upload").length ?? 0;
  const audioCount = videos?.filter((v) => v.videoType === "audio").length ?? 0;

  // Storage bar values
  const usedSeconds = storageData?.usedSeconds ?? 0;
  const limitSeconds = storageData?.limitSeconds ?? 7200;
  const usedMinutes = Math.round(usedSeconds / 60);
  const storagePercent = Math.min(100, (usedSeconds / limitSeconds) * 100);
  const storageColor = storageData?.isAtLimit
    ? "bg-destructive"
    : storageData?.isNearLimit
    ? "bg-yellow-500"
    : "";

  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-5xl mx-auto px-4 py-20 text-center">
          <Library className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Sign in to access your library</h2>
          <p className="text-muted-foreground mb-6">Save your favorite dance videos and practice settings.</p>
          <a href="/auth/google">
            <Button data-testid="button-login-library">Sign in with Google</Button>
          </a>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="text-library-title">My Library</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Your saved dance videos and practice settings
              {videos && !isPaid && (
                <span className="ml-2 text-muted-foreground" data-testid="text-library-count">
                  {uploadCount}/1 upload
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-border overflow-hidden" data-testid="view-mode-toggle">
              <Button
                variant={viewMode === "videos" ? "default" : "ghost"}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode("videos")}
                data-testid="view-mode-videos"
              >
                <Video className="w-4 h-4 mr-1" />
                Videos
              </Button>
              {isPaid ? (
                <Button
                  variant={viewMode === "playlists" ? "default" : "ghost"}
                  size="sm"
                  className="rounded-none"
                  onClick={() => setViewMode("playlists")}
                  data-testid="view-mode-playlists"
                >
                  <List className="w-4 h-4 mr-1" />
                  Playlists
                </Button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-none text-muted-foreground"
                      onClick={() => { setUpgradeFeatureName("playlists"); setUpgradeModalOpen(true); }}
                      data-testid="view-mode-playlists-locked"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      <List className="w-4 h-4 mr-1" />
                      Playlists
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Playlists require a Pro plan</TooltipContent>
                </Tooltip>
              )}
            </div>
            <Button
              data-testid="button-add-video"
              onClick={() => {
                window.location.href = "/?scrollTo=get-started";
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Video
            </Button>
          </div>
        </div>

        {/* Storage bar for paid users */}
        {isPaid && storageData && storagePercent >= 75 && (
          <div className="mb-6" data-testid="storage-bar">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Storage used: {usedMinutes} / 120 min</span>
              {storageData.isAtLimit && <span className="text-destructive font-medium">Limit reached</span>}
              {storageData.isNearLimit && !storageData.isAtLimit && <span className="text-yellow-600 font-medium">Approaching limit</span>}
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${storageColor || "bg-primary"}`}
                style={{ width: `${storagePercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Playlists view */}
        {isPaid && viewMode === "playlists" && (
          <div data-testid="playlists-view">
            <div className="mb-4">
              {creatingPlaylist ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    placeholder="Playlist name..."
                    maxLength={255}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newPlaylistName.trim()) {
                        createPlaylistMutation.mutate(newPlaylistName.trim());
                      }
                      if (e.key === "Escape") {
                        setCreatingPlaylist(false);
                        setNewPlaylistName("");
                      }
                    }}
                    data-testid="input-new-playlist"
                  />
                  <Button
                    size="sm"
                    onClick={() => newPlaylistName.trim() && createPlaylistMutation.mutate(newPlaylistName.trim())}
                    disabled={createPlaylistMutation.isPending}
                    data-testid="button-create-playlist-confirm"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setCreatingPlaylist(false); setNewPlaylistName(""); }}
                    data-testid="button-create-playlist-cancel"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCreatingPlaylist(true)}
                  data-testid="button-new-playlist"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Playlist
                </Button>
              )}
            </div>

            {!playlistsData || playlistsData.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <List className="w-10 h-10 mx-auto mb-3" />
                <p>No playlists yet. Create one to organize your videos.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {playlistsData.map((playlist) => (
                  <Card key={playlist.id} className="p-4" data-testid={`card-playlist-${playlist.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      {editingPlaylistId === playlist.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editPlaylistName}
                            onChange={(e) => setEditPlaylistName(e.target.value)}
                            maxLength={255}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && editPlaylistName.trim()) {
                                renamePlaylistMutation.mutate({ id: playlist.id, name: editPlaylistName.trim() });
                              }
                              if (e.key === "Escape") setEditingPlaylistId(null);
                            }}
                            data-testid={`input-rename-playlist-${playlist.id}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => editPlaylistName.trim() && renamePlaylistMutation.mutate({ id: playlist.id, name: editPlaylistName.trim() })}
                            disabled={renamePlaylistMutation.isPending}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingPlaylistId(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <button
                          className="text-sm font-medium hover:underline text-left flex-1"
                          onClick={() => {
                            setSelectedPlaylistId(playlist.id);
                            setViewMode("videos");
                          }}
                          data-testid={`button-playlist-name-${playlist.id}`}
                        >
                          {playlist.name}
                        </button>
                      )}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => { setEditingPlaylistId(playlist.id); setEditPlaylistName(playlist.name); }}
                          data-testid={`button-rename-playlist-${playlist.id}`}
                        >
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deletePlaylistMutation.mutate(playlist.id)}
                          disabled={deletePlaylistMutation.isPending}
                          data-testid={`button-delete-playlist-${playlist.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Videos view */}
        {viewMode === "videos" && (
          <>
            {selectedPlaylistId !== null && isPaid && (
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-muted-foreground">
                  Filtering by: <strong>{playlistsData?.find(p => p.id === selectedPlaylistId)?.name}</strong>
                </span>
                <Button size="sm" variant="ghost" onClick={() => setSelectedPlaylistId(null)}>
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              </div>
            )}

            {videos && videos.length > 0 && selectedPlaylistId === null && (
              <div className="flex items-center gap-2 mb-6" data-testid="filter-toggle">
                <Button
                  variant={filter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter("all")}
                  data-testid="filter-all"
                >
                  All ({videos.length})
                </Button>
                <Button
                  variant={filter === "youtube" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter("youtube")}
                  data-testid="filter-youtube"
                >
                  YouTube ({youtubeCount})
                </Button>
                <Button
                  variant={filter === "upload" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter("upload")}
                  data-testid="filter-uploads"
                >
                  Uploads ({uploadCount})
                </Button>
                {audioCount > 0 && (
                  <Button
                    variant={filter === "audio" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilter("audio")}
                    data-testid="filter-audio"
                  >
                    Audio ({audioCount})
                  </Button>
                )}
              </div>
            )}

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="p-0 overflow-hidden animate-pulse">
                    <div className="aspect-video bg-muted" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : !filteredVideos || filteredVideos.length === 0 ? (
              <div className="text-center py-20">
                <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2" data-testid="text-empty-library">
                  {filter === "all" ? "No saved videos yet" : `No ${filter === "youtube" ? "YouTube" : "uploaded"} videos`}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                  {filter === "all"
                    ? "Load a YouTube video or upload a file on the home page, then use the Save button to add it to your library."
                    : `You haven't saved any ${filter === "youtube" ? "YouTube" : "uploaded"} videos yet.`}
                </p>
                {filter === "all" ? (
                  <Link href="/">
                    <Button data-testid="button-go-home">Go to Player</Button>
                  </Link>
                ) : (
                  <Button variant="outline" onClick={() => setFilter("all")} data-testid="button-show-all">
                    Show All Videos
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVideos.map((video) => {
                  const thumb = getThumbnail(video);
                  const settings = (video.settings || {}) as any;
                  const isEditing = editingId === video.id;
                  const isNotesOpen = notesOpenId === video.id;
                  const hasNotes = !!(video.notes && video.notes.trim());
                  return (
                    <Card
                      key={video.id}
                      className={`p-0 overflow-hidden group ${isEditing || isNotesOpen ? "" : "cursor-pointer hover-elevate"}`}
                      data-testid={`card-video-${video.id}`}
                      onClick={() => handleOpenVideo(video)}
                    >
                      <div className="aspect-video bg-muted relative">
                        {video.videoType === "audio" ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                            <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                              <Music className="w-7 h-7 text-primary" />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {(video.settings as any)?.tempo && (video.settings as any).tempo !== 1
                                ? `${Math.round((video.settings as any).tempo * 100)}% tempo`
                                : "Audio track"}
                            </span>
                          </div>
                        ) : thumb ? (
                          <img
                            src={thumb}
                            alt={video.title || "Saved video"}
                            className="w-full h-full object-cover"
                            data-testid={`img-thumbnail-${video.id}`}
                          />
                        ) : video.videoType === "upload" && video.cloudflareStreamId ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-black" data-testid={`processing-indicator-${video.id}`}>
                            <Loader2 className="w-8 h-8 animate-spin text-primary mb-2" />
                            <p className="text-white/80 text-xs font-medium">Processing video...</p>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-10 h-10 text-muted-foreground" />
                          </div>
                        )}
                        {video.videoType !== "audio" && (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                        {video.videoType === "audio" && (
                          <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors" />
                        )}
                        <Badge className="absolute top-2 left-2 text-xs no-default-hover-elevate no-default-active-elevate" variant="secondary">
                          {video.videoType === "youtube" ? "YouTube" : video.videoType === "audio" ? "Audio" : "Upload"}
                        </Badge>
                      </div>
                      <div className="p-4">
                        {isEditing ? (
                          <div className="flex items-center gap-1 mb-2" onClick={(e) => e.stopPropagation()}>
                            <Input
                              ref={editInputRef}
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveEdit();
                                if (e.key === "Escape") cancelEdit();
                              }}
                              className="text-sm"
                              maxLength={500}
                              data-testid={`input-rename-${video.id}`}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={saveEdit}
                              disabled={renameMutation.isPending}
                              data-testid={`button-save-rename-${video.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={cancelEdit}
                              data-testid={`button-cancel-rename-${video.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <h3 className="font-medium text-sm truncate mb-2" data-testid={`text-title-${video.id}`}>
                            {video.title || (video.youtubeId ? `YouTube: ${video.youtubeId}` : "Uploaded Video")}
                          </h3>
                        )}
                        {video.videoType === "audio" ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {settings.tempo && settings.tempo !== 1 && (
                              <Badge variant="outline" className="text-xs no-default-hover-elevate no-default-active-elevate">
                                {Math.round(settings.tempo * 100)}% tempo
                              </Badge>
                            )}
                            {settings.pitch != null && settings.pitch !== 0 && (
                              <Badge variant="outline" className="text-xs no-default-hover-elevate no-default-active-elevate">
                                {settings.pitch > 0 ? `+${settings.pitch}` : settings.pitch} st
                              </Badge>
                            )}
                            {settings.audioLoopStart != null && settings.audioLoopEnd != null && (
                              <Badge variant="outline" className="text-xs gap-1 no-default-hover-elevate no-default-active-elevate">
                                <Repeat className="w-3 h-3" /> Loop
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {settings.isMirrored && (
                              <Badge variant="outline" className="text-xs gap-1 no-default-hover-elevate no-default-active-elevate">
                                <FlipHorizontal className="w-3 h-3" /> Mirror
                              </Badge>
                            )}
                            {settings.showLeftRight && (
                              <Badge variant="outline" className="text-xs gap-1 no-default-hover-elevate no-default-active-elevate">
                                <span className="font-bold">L</span><span className="font-bold">R</span>
                              </Badge>
                            )}
                            {settings.playbackSpeed && settings.playbackSpeed !== 1 && (
                              <Badge variant="outline" className="text-xs gap-1 no-default-hover-elevate no-default-active-elevate">
                                <Gauge className="w-3 h-3" /> {settings.playbackSpeed}x
                              </Badge>
                            )}
                            {settings.loopStart != null && settings.loopEnd != null && (
                              <Badge variant="outline" className="text-xs gap-1 no-default-hover-elevate no-default-active-elevate">
                                <Repeat className="w-3 h-3" /> Loop
                              </Badge>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-3 gap-1">
                          <span className="text-xs text-muted-foreground">
                            {video.createdAt ? new Date(video.createdAt).toLocaleDateString() : ""}
                          </span>
                          <div className="flex items-center gap-1">
                            {/* Open in Audio Player button — audio tracks only */}
                            {video.videoType === "audio" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7 px-2 text-primary"
                                data-testid={`button-open-audio-player-${video.id}`}
                                onClick={(e) => { e.stopPropagation(); setLocation(`/audio?saved=${video.id}`); }}
                              >
                                <Music className="w-3 h-3 mr-1" />
                                Open
                              </Button>
                            )}
                            {/* Share button — uploaded videos, paid users only */}
                            {isPaid && video.videoType === "upload" && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={video.isShared ? "text-primary" : ""}
                                    data-testid={`button-share-${video.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Share2 className={`w-4 h-4 ${video.isShared ? "" : "text-muted-foreground"}`} />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-52 p-2" onClick={(e) => e.stopPropagation()}>
                                  <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Share video</p>
                                  {!video.isShared ? (
                                    <button
                                      className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors flex items-center gap-2"
                                      onClick={() => shareMutation.mutate(video.id)}
                                      disabled={shareMutation.isPending}
                                    >
                                      <LinkIcon className="w-4 h-4" />
                                      Create share link
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors flex items-center gap-2"
                                        onClick={() => {
                                          const shareUrl = `${window.location.origin}/shared/${video.shareToken}`;
                                          navigator.clipboard.writeText(shareUrl).then(() => {
                                             trackEvent("share_link_copied", {
                                               source_type: "upload",
                                               location: "library",
                                             });
                                            toast({ title: "Share link copied!" });
                                          });
                                        }}
                                      >
                                        <LinkIcon className="w-4 h-4" />
                                        Copy link
                                      </button>
                                      <button
                                        className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors flex items-center gap-2 text-destructive"
                                        onClick={() => revokeShareMutation.mutate({ id: video.id, isShared: false })}
                                        disabled={revokeShareMutation.isPending}
                                      >
                                        <Unlink className="w-4 h-4" />
                                        Disable sharing
                                      </button>
                                    </>
                                  )}
                                </PopoverContent>
                              </Popover>
                            )}

                            {/* Playlist add button */}
                            {isPaid ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`button-add-to-playlist-${video.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ListPlus className="w-4 h-4 text-muted-foreground" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-2" onClick={(e) => e.stopPropagation()}>
                                  <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Add to playlist</p>
                                  {!playlistsData || playlistsData.length === 0 ? (
                                    <p className="text-xs text-muted-foreground px-1">No playlists yet.</p>
                                  ) : (
                                    playlistsData.map((playlist) => (
                                      <button
                                        key={playlist.id}
                                        className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors"
                                        onClick={() => addToPlaylistMutation.mutate({ playlistId: playlist.id, videoId: video.id })}
                                        data-testid={`add-to-playlist-${playlist.id}-video-${video.id}`}
                                      >
                                        {playlist.name}
                                      </button>
                                    ))
                                  )}
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`button-add-to-playlist-locked-${video.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setUpgradeFeatureName("playlists");
                                      setUpgradeModalOpen(true);
                                    }}
                                  >
                                    <Lock className="w-4 h-4 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Playlists require a Pro plan</TooltipContent>
                              </Tooltip>
                            )}

                            {/* Notes button */}
                            {isPaid ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className={hasNotes ? "text-primary" : ""}
                                data-testid={`button-notes-${video.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleNotes(video);
                                }}
                              >
                                <StickyNote className={`w-4 h-4 ${hasNotes ? "" : "text-muted-foreground"}`} />
                              </Button>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    disabled
                                    data-testid={`button-notes-locked-${video.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Lock className="w-4 h-4 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Notes require a paid plan</TooltipContent>
                              </Tooltip>
                            )}

                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`button-rename-${video.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditing(video);
                              }}
                            >
                              <Pencil className="w-4 h-4 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`button-delete-${video.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMutation.mutate(video.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>

                        {isNotesOpen && isPaid && (
                          <div className="mt-3 border-t border-border pt-3" onClick={(e) => e.stopPropagation()} data-testid={`notes-section-${video.id}`}>
                            <div className="flex items-center justify-between mb-2 gap-1">
                              <span className="text-xs font-medium text-muted-foreground">Notes</span>
                              {notesSaving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                              {!notesSaving && notesDirty && (
                                <span className="text-xs text-muted-foreground">Unsaved</span>
                              )}
                              {!notesSaving && !notesDirty && notesSaved && (
                                <span className="text-xs text-green-500 transition-opacity duration-500">Saved ✓</span>
                              )}
                            </div>
                            <Textarea
                              value={notesText}
                              onChange={(e) => handleNotesChange(video.id, e.target.value)}
                              placeholder="Add your dance notes here... (counts, cues, tips)"
                              className="text-sm resize-none min-h-[80px]"
                              maxLength={5000}
                              data-testid={`textarea-notes-${video.id}`}
                            />
                            <div className="flex items-center justify-between mt-2 gap-1">
                              <span className="text-xs text-muted-foreground">{notesText.length}/5000</span>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={!notesDirty || notesSaving}
                                  onClick={() => {
                                    if (notesSaveTimerRef.current) {
                                      clearTimeout(notesSaveTimerRef.current);
                                      notesSaveTimerRef.current = null;
                                    }
                                    saveNotes(video.id, notesText);
                                  }}
                                  data-testid={`button-save-notes-${video.id}`}
                                >
                                  {notesSaving ? "Saving..." : "Save"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleNotes(video)}
                                  data-testid={`button-close-notes-${video.id}`}
                                >
                                  Done
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        mode="upgrade"
        featureName={upgradeFeatureName}
      />
    </div>
  );
}
